import { RESULT_PREVIEW_BASIC_PASSWORD, RESULT_PREVIEW_BASIC_USERNAME } from "../constants/resultPreviewAuth";
import { httpClient } from "../http/client";

function guestAuthorizationHeader(): string {
  const credentials = `${RESULT_PREVIEW_BASIC_USERNAME}:${RESULT_PREVIEW_BASIC_PASSWORD}`;
  return `Basic ${btoa(credentials)}`;
}

export interface ReferenceData {
  categories: string[];
  genders: string[];
  exams: string[];
  version?: string[];
}

interface ReferenceDataCache {
  fetchedAt: string;
  data: ReferenceData;
}

const STORAGE_KEY = "hallticket.referenceData.v1";

function canUseLocalStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function getReferenceDataFromStorage(): ReferenceData | null {
  if (!canUseLocalStorage()) {
    return null;
  }

  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<ReferenceDataCache> | ReferenceData;

    // Backwards compatible: allow storing the raw payload directly.
    const data =
      parsed && typeof parsed === "object" && "data" in parsed
        ? (parsed as ReferenceDataCache).data
        : (parsed as ReferenceData);

    if (!data || typeof data !== "object") {
      return null;
    }

    const categories = Array.isArray(data.categories) ? data.categories.filter((v): v is string => typeof v === "string") : [];
    const genders = Array.isArray(data.genders) ? data.genders.filter((v): v is string => typeof v === "string") : [];
    const exams = Array.isArray(data.exams) ? data.exams.filter((v): v is string => typeof v === "string") : [];
    const version = Array.isArray(data.version) ? data.version.filter((v): v is string => typeof v === "string") : [];

    if (categories.length === 0 && genders.length === 0 && exams.length === 0) {
      return null;
    }

    return {
      categories,
      genders,
      exams,
      ...(version.length > 0 ? { version } : {}),
    };
  } catch {
    return null;
  }
}

export function setReferenceDataInStorage(data: ReferenceData): void {
  if (!canUseLocalStorage()) {
    return;
  }

  const payload: ReferenceDataCache = {
    fetchedAt: new Date().toISOString(),
    data,
  };

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

export async function fetchReferenceData(): Promise<ReferenceData> {
  const response = await httpClient.get<ReferenceData>("/api/v1/reference-data", {
    headers: {
      Accept: "application/json",
      Authorization: guestAuthorizationHeader(),
    },
  });
  return response.data;
}

export async function ensureReferenceDataCached(): Promise<ReferenceData | null> {
  const existing = getReferenceDataFromStorage();
  try {
    const next = await fetchReferenceData();
    setReferenceDataInStorage(next);
    return next;
  } catch {
    return existing;
  }
}

export type ReferenceReservationType = "Vertical" | "Horizontal";

export interface ReferenceDataReservation {
  id: number;
  type: ReferenceReservationType;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

function normalizeReferenceDataReservations(payload: unknown): ReferenceDataReservation[] {
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload
    .map((item) => {
      if (!item || typeof item !== "object") {
        return null;
      }

      const typed = item as Record<string, unknown>;
      const id = typeof typed.id === "number" && Number.isFinite(typed.id) ? typed.id : null;
      const type = typed.type === "Vertical" || typed.type === "Horizontal" ? typed.type : null;
      const name = typeof typed.name === "string" ? typed.name.trim() : "";
      const description = typeof typed.description === "string" ? typed.description : "";
      const createdAt = typeof typed.createdAt === "string" ? typed.createdAt : "";
      const updatedAt = typeof typed.updatedAt === "string" ? typed.updatedAt : "";

      if (id === null || !type || !name) {
        return null;
      }

      return {
        id,
        type,
        name,
        description,
        createdAt,
        updatedAt,
      } satisfies ReferenceDataReservation;
    })
    .filter((item): item is ReferenceDataReservation => item !== null)
    .sort((a, b) => a.id - b.id);
}

export async function fetchReferenceDataReservations(): Promise<ReferenceDataReservation[]> {
  const response = await httpClient.get<unknown>("/api/v1/reference-data/reservations", {
    headers: {
      Accept: "application/json",
      Authorization: guestAuthorizationHeader(),
    },
  });
  return normalizeReferenceDataReservations(response.data);
}

export async function fetchVerticalReferenceReservations(): Promise<ReferenceDataReservation[]> {
  const reservations = await fetchReferenceDataReservations();
  return reservations.filter((item) => item.type === "Vertical");
}

export async function fetchHorizontalReferenceReservations(): Promise<ReferenceDataReservation[]> {
  const reservations = await fetchReferenceDataReservations();
  return reservations.filter((item) => item.type === "Horizontal");
}

